import { useEffect, useState, useCallback } from "react";
import Header from "../../Components/Header";
import Button from "../../Components/Button";
import { Edit2, Global, Profile2User, Trash, Warning2 } from "iconsax-react";
import { QRCodeSVG } from "qrcode.react";
import { useToast } from "../../Components/Toast";
import { toPng } from "html-to-image";
import TextboxSet from "../../Components/TextboxSet";
import Modal from "../../Components/Modal";
import LocationSet from "../../Components/LocationSet";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  LOW_END_CHECKIN,
  LOW_END_EVENT_LIST,
  LOW_END_GUEST_LIST,
} from "../../Router/path";
import {
  DELETE_EVENT_API,
  LE_EVENT_INFO_API,
  LE_UPDATE_EVENT_API,
} from "../../Constant/api";
import LoadingInComponent from "../../Components/LoadingInComponent";

const LEEventInfo = () => {
  const [eventInfo, setEventInfo] = useState(null);
  const [eventUpdateForm, setEventUpdateForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const navigate = useNavigate();
  const { toast } = useToast();

  const handleOnChange = (e) => {
    setEventUpdateForm({ ...eventUpdateForm, [e.target.name]: e.target.value });
  };

  const fetchEventInfo = useCallback(() => {
    setLoading(true);
    axios
      .get(LE_EVENT_INFO_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: { event_id: sessionStorage.getItem("event_id") },
      })
      .then((response) => {
        setEventInfo(response.data.data);
        setLoading(false);
      })
      .catch((error) => {
        toast(
          error.response?.data?.message || "Failed to fetch event details",
          "error",
        );
        console.error("Error fetching event details:", error);
        setLoading(false);
      });
  }, [toast]);

  const updateDetails = () => {
    const payload = {
      event_id: eventInfo.id,
      name: eventUpdateForm.name,
      venue: eventUpdateForm.venue,
      date: eventUpdateForm.date,
      time: eventUpdateForm.time,
    };

    setLoading(true);
    axios
      .put(LE_UPDATE_EVENT_API, payload, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
      })
      .then((response) => {
        toast("Event updated successfully", "success");
        setShowModal(false);
        fetchEventInfo();
        setLoading(false);
      })
      .catch((error) => {
        toast(
          error.response?.data?.message || "Failed to update event",
          "error",
        );
        setLoading(false);
        console.error("Error updating event:", error);
      });
  };

  const handleDeleteEvent = () => {
    setLoading(true);
    axios
      .delete(DELETE_EVENT_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: {
          eventId: sessionStorage.getItem("event_id"),
        },
      })
      .then((response) => {
        setLoading(false);
        sessionStorage.removeItem("event_id");
        sessionStorage.removeItem("dietary");
        sessionStorage.removeItem("seat");
        navigate(LOW_END_EVENT_LIST);
        toast("Event deleted successfully", "success");
      })
      .catch((error) => {
        setLoading(false);
        toast("Error deleting event", "error");
      });
  };

  useEffect(() => {
    if (!eventInfo) {
      fetchEventInfo();
    }
  }, [eventInfo, fetchEventInfo]);

  return (
    <div className="flex flex-col w-full min-h-screen">
      {showModal && (
        <Modal
          title="Update Event"
          toggleModal={() => setShowModal(!showModal)}
          submitLabel="Update"
          loading={loading}
          handleSubmit={updateDetails}
        >
          <div className="flex flex-col w-full h-full gap-[24px]">
            <div className="flex flex-col md:flex-row w-full gap-[24px]">
              <TextboxSet
                onChange={handleOnChange}
                value={eventUpdateForm.name}
                type="text"
                label="Event Title"
                placeholder="Event Title"
                name="name"
                required
              />
              <LocationSet
                onChange={handleOnChange}
                value={eventUpdateForm.venue}
                name="venue"
                placeholder="Venue"
              />
            </div>
            <div className="flex flex-col md:flex-row w-full gap-[24px]">
              <TextboxSet
                onChange={handleOnChange}
                type="date"
                label="Date"
                name="date"
                value={eventUpdateForm.date}
              />
              <TextboxSet
                onChange={handleOnChange}
                type="time"
                label="Time"
                name="time"
                value={eventUpdateForm.time}
              />
            </div>
          </div>
        </Modal>
      )}

      {showConfirmationModal && (
        <Modal
          title="Delete Event"
          confirmation={handleDeleteEvent}
          toggleModal={() => setShowConfirmationModal(!showConfirmationModal)}
          submitLabel="Delete"
        >
          <div className="flex flex-col w-full items-center justify-center gap-[16px] py-[32px]">
            <Warning2 size="64" color="#ff0000" variant="Bold" />
            <p>Are you sure you want to delete this event?</p>
          </div>
        </Modal>
      )}

      <Header noSidebar={true} />

      <LoadingInComponent isLoading={loading}>
        {eventInfo && (
          <div className="flex flex-col w-full h-fit min-h-[calc(70%)] lg:px-[128px] items-center p-[16px] md:p-[32px] gap-[16px] md:gap-[32px] bg-[#F7F8F9]">
            <div className="flex flex-col w-full justify-center items-center gap-2">
              <h1 className="text-[29px]">{eventInfo.name}</h1>
              <div className="flex flex-col items-center justify-center">
                <p className="text-[#666666]">
                  {eventInfo.date ?? "No Date Set"}
                </p>
                <p className="text-[#666666]">
                  {eventInfo.time ?? "No Time Set"}
                </p>
                <p className="text-[#666666]">
                  {eventInfo.venue ?? "No Venue Set"}
                </p>
                <Edit2
                  size="24"
                  color="#666666"
                  variant="Bold"
                  className="cursor-pointer hover:opacity-60"
                  onClick={() => {
                    setEventUpdateForm(eventInfo);
                    setShowModal(!showModal);
                  }}
                />
              </div>
            </div>
            <div className="flex flex-col w-full max-w-[600px] gap-6 items-center">
              <div className="flex flex-col w-full bg-white border border-[#EFF0F2] p-6 rounded-lg gap-2">
                <div className="flex items-center justify-between">
                  <h2 className="text-[20px] font-semibold">Event QR Code</h2>
                  <Button
                    variant="tertiary"
                    onClick={() => {
                      const node = document.getElementById("qr-code");
                      toast("Downloading QR Code...", "info");
                      if (node) {
                        toPng(node)
                          .then((dataUrl) => {
                            const link = document.createElement("a");
                            link.download = `qrcode[${eventInfo.name}].png`;
                            link.href = dataUrl;
                            link.click();
                          })
                          .catch((error) => {
                            toast("Failed to download QR Code", "error");
                            console.error(error);
                          });
                      }
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-4 h-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                      />
                    </svg>
                    Download QR
                  </Button>
                </div>
                <div className="flex items-center justify-center bg-[#EFEDFE] rounded-lg p-4">
                  <div className="p-4 rounded-lg bg-white" id="qr-code">
                    <QRCodeSVG
                      value={`${window.location.origin}${LOW_END_CHECKIN}?event_id=${eventInfo.id}`}
                      bgColor={"white"}
                      fgColor={"black"}
                      size={"256"}
                      level={"H"}
                      imageSettings={{
                        height: 40,
                        width: 40,
                        src: "/images/logo.svg",
                      }}
                    />
                  </div>
                </div>
                <p className="text-[12px] font-normal text-[#666666]">
                  Share this QR code with guests or download it for later use.
                </p>
              </div>

              <div className="flex flex-col w-full h-fit bg-white border border-[#EFF0F2] p-6 rounded-lg gap-4">
                <h2 className="font-semibold text-[20px]">Quick Actions</h2>
                <div className="flex flex-col gap-3">
                  <Button
                    className="w-full"
                    onClick={() => {
                      navigate(LOW_END_GUEST_LIST);
                    }}
                  >
                    <Profile2User size="20" variant="Bold" />
                    Manage Guest List
                  </Button>
                  <Button
                    className="w-full"
                    variant="secondary"
                    onClick={() => {
                      window.open(
                        `${LOW_END_CHECKIN}?event_id=${eventInfo.id}`,
                        "_blank",
                      );
                    }}
                  >
                    <Global size="20" variant="Bold" />
                    View Live Site
                  </Button>
                  <div className="w-full border-b border-gray-200" />
                  <Button
                    className="w-full"
                    variant="error"
                    onClick={() => {
                      setShowConfirmationModal(!showConfirmationModal);
                    }}
                  >
                    <Trash size="20" variant="Bold" />
                    Delete Event
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </LoadingInComponent>
    </div>
  );
};

export default LEEventInfo;
